<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="hidden md:block"></div>
            <h1 class="text-3xl text-center">
                Categorías en total
                <strong class="text-theme-primary">
                    {{ itemsNumber }}
                </strong>
            </h1>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="
                        $ability.hasPermission('line:Create') && !relevanceMode
                    "
                    color="success"
                    size="large"
                    @click="$router.push('/app/lines/new')"
                >
                    <span class="hidden md:inline">Nueva categoría</span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="flex flex-wrap mt-2">
            <div v-if="!relevanceMode" class="w-full mt-3">
                <lines-filters
                    v-model="query"
                    :enabled-filters="$data.$enabledFilters"
                    :default-filters="$data.$defaultFilters"
                    @enter="reloadItems"
                />
                <div class="flex justify-center">
                    <app-button v-show="query" relief @click="reloadItems">
                        Consultar 🔎
                    </app-button>
                </div>
            </div>
            <div
                class="flex flex-col lg:flex-row items-center justify-between w-full mt-3 gap-4"
            >
                <div
                    class="flex flex-wrap items-center justify-center lg:justify-start gap-2"
                >
                    <tooltip-button
                        v-show="
                            selected.length &&
                            $ability.hasPermission('line:Create') &&
                            !relevanceMode
                        "
                        icon="🗂️"
                        message="Clonar categoría"
                        @click="cloneSelected"
                    />
                </div>

                <div
                    class="flex flex-wrap items-center justify-center lg:justify-end gap-3"
                >
                    <div v-if="relevanceMode" class="flex items-center gap-2">
                        <custom-checkbox v-model="reorderAll">
                            Afectar todo
                        </custom-checkbox>
                        <app-button color="success" @click="saveRelevance">
                            Guardar orden 💾
                        </app-button>
                    </div>

                    <app-button
                        v-if="$ability.hasPermission('line:Update')"
                        :color="relevanceMode ? 'warn' : 'primary'"
                        @click="toggleRelevanceMode"
                    >
                        {{ relevanceMode ? "⚖️ Salir" : "⚖️ Editar orden" }}
                    </app-button>

                    <list-settings-button
                        v-if="!relevanceMode"
                        :value="{ pageSize }"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />

                    <tooltip-redirect
                        v-if="
                            $ability.hasPermission('line:Create') &&
                            !relevanceMode
                        "
                        to="/app/lines/new"
                        message="Registrar categoría"
                    />
                </div>
            </div>
        </div>

        <div class="w-full mt-2">
            <custom-sortable-table
                ref="selectableTable"
                :fields="activeFields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                :selectable="!relevanceMode"
                :draggable="relevanceMode"
                :external-current-page="currentPage"
                :external-total-pages="pagesNumber"
                :row-actions="rowActions"
                select-mode="multi"
                responsive
                no-border-collapse
                hover
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                @flex-selected="onRowSelected"
                @drag-drop="onDragDrop"
                @row-send-to-page="onRowSendToPage"
                @row-action="onRowAction"
            >
                <template #cell(slug)="data">
                    <router-link
                        :to="`/app/lines/detail/${data.item.slug}`"
                        class="flex flex-col items-center font-mono rounded-full w-min-16 px-1 py-1 text-white"
                        :class="
                            data.item.active
                                ? 'bg-theme-success'
                                : 'bg-theme-disabled'
                        "
                    >
                        <span class="text-xs font-mono">
                            {{ data.item.slug | cut(30, false) }}
                        </span>
                    </router-link>
                </template>

                <template #cell(name)="data">
                    <span class="text-center">
                        {{ data.item.name | cut(36) }}
                    </span>
                </template>

                <template #cell(summary)="data">
                    <small class="text-left break-all">
                        {{ data.item.summary | cut(30) }}
                    </small>
                </template>
                <template #cell(parentSlug)="data">
                    <div class="flex flex-col items-start gap-1">
                        <span
                            class="rounded-full px-2 py-0.5 text-xs font-semibold"
                            :class="
                                data.item.parentSlug
                                    ? 'bg-theme-hover text-theme-muted border border-theme-border'
                                    : 'bg-theme-primary text-theme-inverse'
                            "
                        >
                            {{
                                data.item.parentSlug
                                    ? `Subcategoría de ${data.item.parentSlug}`
                                    : "Principal"
                            }}
                        </span>
                        <span
                            v-if="data.item.highlightType === 'highlight'"
                            class="rounded-full px-2 py-0.5 text-xs font-semibold bg-theme-warning text-theme-inverse"
                        >
                            ⭐ Destacado
                        </span>
                    </div>
                </template>

                <template #cell(updatedAt)="data">
                    <small class="text-center">
                        {{ data.item.updatedAt | date }}
                    </small>
                </template>
            </custom-sortable-table>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { REORDER_PAGE_SIZE } from "@/common/constants";

import LinesFilters from "@/components/lines/LinesFilters.vue";
import CustomSortableTable from "@/components/utils/CustomSortableTable.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";
import TooltipRedirect from "@/components/utils/TooltipRedirect.vue";

export default {
    name: "LinesIndex",
    components: {
        LinesFilters,
        TooltipButton,
        TooltipRedirect,
        CustomSortableTable
    },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    data: () => ({
        query: { type: "all" },
        itemsCurrent: [],
        selected: [],
        $fields: [
            { key: "slug", label: "Slug", sortable: true },
            { key: "name", label: "Nombre", sortable: true },
            {
                key: "summary",
                label: "Descripción",
                sortable: false
            },
            { key: "parentSlug", label: "Tipo", sortable: true },
            { key: "updatedAt", label: "📆 Actualización", sortable: true }
        ],
        $enabledFilters: ["keyword", "active", "highlightType", "type", "line"],
        $defaultFilters: ["keyword", "type"],
        $urlPaginationKey: "page",
        $urlSortKey: "sort",
        $urlFilterSchema: [
            { key: "keyword", type: "string" },
            { key: "active", type: "boolean" },
            { key: "highlightType", type: "string" },
            { key: "type", type: "string" },
            { key: "lineSlug", type: "string" }
        ],
        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        sortConfig: { orderBy: "updatedAt", orderType: "DESC" },
        relevanceMode: false,
        reorderAll: false,
        pageSize: 24
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        listPageSize() {
            return this.relevanceMode ? REORDER_PAGE_SIZE : this.pageSize;
        },
        rowActions() {
            const actions = [
                { key: "open", icon: "🔗", label: "Abrir en nueva pestaña" }
            ];
            if (this.selected.length > 1) {
                return actions;
            }
            if (this.$ability.hasPermission("line:Create")) {
                actions.push({
                    key: "clone",
                    icon: "🗂️",
                    label: "Clonar categoría"
                });
            }
            return actions;
        },
        activeFields() {
            if (this.relevanceMode) {
                return [
                    {
                        key: "weight",
                        label: "Peso",
                        sortable: false,
                        thClass: "w-14",
                        tdClass: "w-14"
                    },
                    {
                        key: "slug",
                        label: "Slug",
                        sortable: false,
                        thClass: "w-40",
                        tdClass: "w-40"
                    },
                    {
                        key: "name",
                        label: "Nombre",
                        sortable: false,
                        thClass: "w-96",
                        tdClass: "w-96"
                    },
                    {
                        key: "updatedAt",
                        label: "📆 Actualización",
                        sortable: false,
                        thClass: "w-36",
                        tdClass: "w-36"
                    }
                ];
            }
            return this.$data.$fields;
        }
    },
    watch: {},
    async mounted() {
        this.listItems(true);
        this._clickOutsideHandler = (e) => {
            if (
                this.selected.length &&
                this.$refs.selectableTable &&
                !this.$refs.selectableTable.$el.contains(e.target)
            ) {
                this.clearSelected();
            }
        };
        document.addEventListener("click", this._clickOutsideHandler);
    },
    beforeDestroy() {
        document.removeEventListener("click", this._clickOutsideHandler);
    },
    methods: {
        ...mapActions("lines", ["listLinesPaginated", "updateLinesWeights"]),
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } = await this.listLinesPaginated({
                    ...this.query,
                    page: this.currentPage,
                    itemsByPage: this.listPageSize,
                    complete,
                    orderBy: this.sortConfig.orderBy,
                    orderType: this.sortConfig.orderType
                });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize }) {
            this.pageSize = pageSize;
            this.reloadItems();
        },
        onRowSelected(items) {
            this.selected = items;
        },
        clearSelected() {
            if (this.$refs.selectableTable) {
                this.$refs.selectableTable.clearSelected();
            }
            this.selected = [];
        },
        cloneSelected() {
            window
                .open(`/app/lines/clone/${this.selected[0].slug}`, "_blank")
                .focus();
        },
        onDragDrop(reorderedItems) {
            const offset = (this.currentPage - 1) * this.listPageSize;
            this.itemsCurrent = reorderedItems.map((item, index) => ({
                ...item,
                weight: offset + index + 1
            }));
        },
        async onRowSendToPage({ item, page }) {
            if (page === this.currentPage) return;
            const targetWeight = page * this.listPageSize - 1;
            this.itemsCurrent = this.itemsCurrent.filter(
                (p) => p.slug !== item.slug
            );
            this.$loading.show();
            try {
                await this.updateLinesWeights({
                    lines: [{ slug: item.slug, weight: targetWeight }]
                });
                this.$sound.play();
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.itemsCurrent = [...this.itemsCurrent, item];
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onRowAction({ action, item }) {
            switch (action) {
                case "open":
                    window.open(`/app/lines/detail/${item.slug}`, "_blank");
                    break;
                case "clone":
                    window.open(`/app/lines/clone/${item.slug}`, "_blank");
                    break;
            }
        },
        toggleRelevanceMode() {
            this.relevanceMode = !this.relevanceMode;
            if (this.relevanceMode) {
                this.sortConfig = { orderBy: "weight", orderType: "ASC" };
                this.query = { active: true, type: "line" };
                this.clearSelected();
            } else {
                this.sortConfig = { orderBy: "updatedAt", orderType: "DESC" };
                this.query = { type: "line" };
                this.reorderAll = false;
            }
            this.currentPage = 1;
            this.listItems(true);
        },
        async saveRelevance() {
            this.$loading.show();
            const offset = (this.currentPage - 1) * this.listPageSize;
            try {
                await this.updateLinesWeights({
                    lines: this.itemsCurrent.map((item, index) => ({
                        slug: item.slug,
                        weight: offset + index + 1
                    })),
                    reorder: this.reorderAll || undefined
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Relevancia actualizada",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
